import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { loadPaymentWidget, PaymentWidgetInstance } from '@tosspayments/payment-widget-sdk';

interface TossPaymentProps {
  amount: number;
  orderId: string;
  orderName: string;
  customerName: string;
  customerEmail: string;
  onSuccess: (paymentKey: string, orderId: string, amount: number) => void;
  onError: (error: Error) => void;
}

const TossPayment: React.FC<TossPaymentProps> = ({
  amount,
  orderId,
  orderName,
  customerName,
  customerEmail,
  onSuccess,
  onError,
}) => {
  const { t } = useTranslation();
  const paymentWidgetRef = useRef<PaymentWidgetInstance | null>(null);
  const paymentMethodsWidgetRef = useRef<any>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isReady, setIsReady] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');

  useEffect(() => {
    const clientKey = process.env.REACT_APP_TOSS_CLIENT_KEY;

    if (!clientKey) {
      console.error('Toss Payments client key is not set');
      onError(new Error(t('payment.invalidConfig')));
      return;
    }

    const initializePayment = async () => {
      try {
        // Payment Widget 초기화 (비회원 결제이므로 ANONYMOUS 사용)
        const paymentWidget = await loadPaymentWidget(clientKey, 'ANONYMOUS');
        paymentWidgetRef.current = paymentWidget;

        // 결제 수단 UI 렌더링 (동기 반환)
        const paymentMethodsWidget = paymentWidget.renderPaymentMethods(
          '#payment-widget',
          { value: amount },
          { variantKey: 'DEFAULT' }
        );
        paymentMethodsWidgetRef.current = paymentMethodsWidget;

        // 약관 UI 렌더링 (동기 반환)
        paymentWidget.renderAgreement('#agreement', { variantKey: 'DEFAULT' });

        // 'ready' 이벤트를 기다림 - 이제 결제 UI가 완전히 렌더링됨
        paymentMethodsWidget.on('ready', () => {
          console.log('Payment widget is ready');
          setIsReady(true);
          setIsLoading(false);
        });
      } catch (error) {
        console.error('Payment widget initialization error:', error);
        const errorMsg = error instanceof Error ? error.message : t('payment.unknownError');

        // 401 에러인 경우 API 키 문제로 판단
        if (errorMsg.includes('401') || errorMsg.includes('Unauthorized') || errorMsg.includes('알 수 없는')) {
          setErrorMessage(t('payment.invalidTossKey'));
        } else {
          setErrorMessage(errorMsg);
        }

        setHasError(true);
        setIsLoading(false);
      }
    };

    initializePayment();
  }, [amount, customerEmail, onError, t]);

  const handlePayment = async () => {
    const paymentWidget = paymentWidgetRef.current;

    if (!paymentWidget) {
      alert(t('payment.widgetLoading'));
      return;
    }

    if (!isReady) {
      alert(t('payment.uiPreparingRetry'));
      return;
    }

    try {
      // 결제 요청
      await paymentWidget.requestPayment({
        orderId,
        orderName,
        successUrl: `${window.location.origin}/payment/success`,
        failUrl: `${window.location.origin}/payment/fail`,
      });
    } catch (error) {
      console.error('Payment request error:', error);
      if (error instanceof Error) {
        onError(error);
      }
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold mb-6">{t('payment.payTitle')}</h2>

      {hasError ? (
        /* API 키 오류 시 표시 */
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start">
              <svg className="w-6 h-6 text-red-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-red-800 mb-2">{t('payment.widgetInitFailed')}</h3>
                <p className="text-sm text-red-700 mb-4">{errorMessage}</p>
                <div className="bg-white rounded p-4 text-sm">
                  <p className="font-semibold text-gray-900 mb-2">{t('payment.solutionTitle')}</p>
                  <ol className="list-decimal list-inside space-y-1 text-gray-700">
                    <li>
                      <a
                        href="https://developers.tosspayments.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:underline"
                      >
                        {t('payment.tossDeveloperCenter')}
                      </a>
                      {t('payment.solutionLogin')}
                    </li>
                    <li>{t('payment.solutionIssueKey')}</li>
                    <li>{t('payment.solutionReplaceKey')}</li>
                    <li>{t('payment.solutionRestart')}</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          {/* 결제 금액 표시 (에러 상태에서도 표시) */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold">{t('payment.finalAmount')}</span>
              <span className="text-2xl font-bold text-primary-600">
                {amount.toLocaleString()}{t('common.won')}
              </span>
            </div>
          </div>

          {/* 현재 설정된 키 정보 (디버깅용) */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs text-gray-600">
            <p className="font-semibold mb-1">{t('payment.currentClientKey')}</p>
            <code className="bg-gray-100 px-2 py-1 rounded">
              {process.env.REACT_APP_TOSS_CLIENT_KEY || t('payment.notConfigured')}
            </code>
          </div>
        </div>
      ) : (
        <div className="relative">
          {/* 로딩 오버레이 */}
          {isLoading && (
            <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-10">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          )}

          {/* 결제 위젯 렌더링 영역 */}
          <div id="payment-widget" className="mb-6"></div>

          {/* 약관 영역 */}
          <div id="agreement" className="mb-6"></div>

          {/* 결제 금액 표시 */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold">{t('payment.finalAmount')}</span>
              <span className="text-2xl font-bold text-primary-600">
                {amount.toLocaleString()}{t('common.won')}
              </span>
            </div>
          </div>

          {/* 결제하기 버튼 */}
          <button
            onClick={handlePayment}
            disabled={isLoading || !isReady}
            className="w-full py-4 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-lg font-semibold rounded-lg transition-colors"
          >
            {isLoading || !isReady ? t('payment.paymentPreparing') : t('payment.payAmount', { amount: amount.toLocaleString() })}
          </button>

          {/* 안내 문구 */}
          <div className="mt-6 space-y-2">
            <p className="text-sm text-gray-500 text-center">
              {t('payment.securePaymentNotice')}
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-xs text-yellow-800">
                <span className="font-semibold">{t('payment.testMode')}:</span> {t('payment.testModeDesc')}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TossPayment;
